import {
  BehaviorSubject,
  catchError,
  delay,
  EMPTY,
  filter,
  from,
  mergeMap,
  Observable,
  of,
} from 'rxjs';
import { Dependencies } from '../../../../configurations/dependencies/dependencies.interface';
import { Action, AnyAction } from '../../../state/action.type';
import { AppState } from '../../../state/app.state';
import {
  retrieveFighterImage,
  retrieveFighterImageFailure,
  retrieveFighterImageSuccess,
} from '../../../state/image/image.action';
import {
  RetrieveFighterImage,
  RETRIEVE_FIGHTER_IMAGE,
} from '../../../state/image/image.action.type';
import { FIGHTER_USECASE_ERROR_MESSAGES } from '../../errors/usecases.errorMessages/fighterUsecase.error.messages';
import {
  buildFighterKey,
  getDefaultFighterImage,
} from '../../utils/fighterImage.utils';

const DEFAULT_RETRY_DELAY = 300;

export const retrieveFighterImageUsecase = (
  action$: Observable<Action>,
  _state$: BehaviorSubject<AppState>,
  dependencies: Dependencies,
): Observable<Action> =>
  action$.pipe(
    filter(
      (action: AnyAction): action is RetrieveFighterImage =>
        action.type === RETRIEVE_FIGHTER_IMAGE,
    ),
    mergeMap((action: RetrieveFighterImage) => {
      const { firstName, lastName, dob } = action.payload;

      // Use the new utility function to build the key
      const imagePath = buildFighterKey(firstName, lastName, dob);

      if (!imagePath) {
        console.warn(
          'retrieveFighterImageUsecase: Missing required fighter data, using default image',
        );
        // Store default image in state to avoid repeated calculations
        return [
          retrieveFighterImageSuccess({
            imagePath: `${firstName?.toLowerCase() || ''}_${lastName?.toLowerCase() || ''}_${dob}`,
            image: getDefaultFighterImage(action.payload.gender),
          }),
          retrieveFighterImageFailure(
            'Invalid fighter data: missing required fields',
          ),
        ];
      }

      if (_state$.value.image.fighterImages[imagePath]) return EMPTY;

      return from(
        dependencies.fighterImageGateway.getFighterImage(
          dob,
          firstName?.toLowerCase(),
          lastName?.toLowerCase(),
        ),
      ).pipe(
        mergeMap((image: string) => {
          return [
            retrieveFighterImageSuccess({
              imagePath: imagePath,
              image: image,
            }),
          ];
        }),
        catchError((error: Error) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const remainingRetries = action.payload.retry
            ? action.payload.retry - 1
            : 0;
          if (remainingRetries) {
            return of(
              retrieveFighterImage({
                firstName: action.payload.firstName,
                lastName: action.payload.lastName,
                dob: action.payload.dob,
                gender: action.payload.gender,
                retry: remainingRetries,
              }),
            ).pipe(delay(DEFAULT_RETRY_DELAY));
          }
          // Store default image for failed API calls too
          return [
            retrieveFighterImageSuccess({
              imagePath: imagePath,
              image: getDefaultFighterImage(action.payload.gender),
            }),
            retrieveFighterImageFailure(
              FIGHTER_USECASE_ERROR_MESSAGES.RETRIEVAL_FAILED,
            ),
          ];
        }),
      );
    }),
  );
